import { TargetStatusRequestTargetKind } from '@/api-client/target/model';
import {
  CONTAINER_DETAIL,
  KUBER_DETAIL,
  PA_KUBER_DETAIL,
} from '@/common/define/slideDetail.define';
import { useMessage } from '@/common/inject/useMessage';
import { useInternational } from '@/common/locale';
import { useRolePermission, useTargetPermissionCheck } from '@/common/permission/permission.utils';
import {
  DetailTypeToPermissionKey,
  getSlideDetailStore,
  PanelInfo,
  TOTAL_DETAIL,
} from '@/common/stores/slide-detail';
import {
  SlideDetailAdapterParams,
  SlideDetailAdapterResult,
  SlideDetailAdapterResultKey,
} from '@/common/types/slideDetailAdapter.types';
import { createSlideDetailResult } from '@/common/utils/slideDetailAdapterResult';
import { RolePermissionKeyType } from '@/config/views/permission/rolePermission/rolePermission.type';
import { merge } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { z } from 'zod';
import { OBJECT_KIND_TO_KUBERNETES_DETAIL } from '../utils/define';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';

/** 쿠버네티스의 도메인 내 타입 에서만 타입 지정하도록 제약 조건 추가*/
type KuberDetailType = (typeof KUBER_DETAIL)[keyof typeof KUBER_DETAIL];
type PaKuberDetailType = (typeof PA_KUBER_DETAIL)[keyof typeof PA_KUBER_DETAIL];
type ContainerDetailType = (typeof CONTAINER_DETAIL)[keyof typeof CONTAINER_DETAIL];

type KubernetesSlideDetailType = KuberDetailType | PaKuberDetailType | ContainerDetailType;

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

export type KubernetesPanelInfo = DistributiveOmit<
  PanelInfo<KubernetesSlideDetailType>,
  'init' | 'selectedTab' | 'currentState' | 'state'
>;

export type KubernetesAdapterInput = {
  type: KubernetesSlideDetailType;
  name?: string;
  container?: string;
  addition?: Record<string, unknown>;
  targetId?: string;
  targetName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rawData?: Record<string, any>;
  /** 컨텍스트 타임 피커에서 선택한 시간 범위 추가 필드 - UTC0 시간 형식*/
  fromTime?: string;
  toTime?: string;
  /** KubernetesPanelInfo 유니온 타입의 추가 필드 허용 (title, service, ingress 등) */
  [key: string]: unknown;
};

/** field meta satisfies 검증용 (index signature 제외) */
export type KubernetesFieldMetaInput = Pick<KubernetesAdapterInput, 'targetId' | 'targetName'> & {
  collectTime?: string;
};

const normalizeK8sInput = (input: KubernetesAdapterInput): KubernetesPanelInfo => {
  const { targetId, targetName, rawData, ...rest } = input;
  const addition = rest.addition as Record<string, unknown> | undefined;
  const isContainerType =
    rest.type === TOTAL_DETAIL.K8S_CONTAINER || rest.type === TOTAL_DETAIL.CONTAINER_HOST;
  const isEventType = rest.type === TOTAL_DETAIL.EVENT;

  return {
    ...rest,
    ...(typeof addition?.fromTime === 'string' && typeof addition?.toTime === 'string'
      ? { fromTime: addition.fromTime, toTime: addition.toTime }
      : {}),
    ...(isContainerType && !rest.container && targetId ? { container: targetId } : {}),
    name: rest.name || rawData?.name || targetName || '',
    addition: {
      ...addition,
      uid: addition?.uid || rawData?.uid || targetId || '',
      ...(isEventType && !addition?.targetId && targetId ? { targetId } : {}),
    },
  } as KubernetesPanelInfo;
};

type KubernetesSlideDetailAdditionKey =
  PanelInfo<KubernetesSlideDetailType> extends infer P
    ? P extends { addition: infer A }
      ? A extends object
        ? keyof A
        : never
      : never
    : never;

const buildEnrichedParams = (
  params: KubernetesAdapterInput,
  contextAddition:
    | (KubernetesPanelInfo['addition'] & { fromTime?: string; toTime?: string })
    | undefined,
): KubernetesAdapterInput => {
  const contextFromTime =
    typeof contextAddition?.fromTime === 'string' ? contextAddition.fromTime : undefined;
  const contextToTime =
    typeof contextAddition?.toTime === 'string' ? contextAddition.toTime : undefined;

  const utcFromTime = (params.fromTime as string | undefined) || contextFromTime;
  const utcToTime = (params.toTime as string | undefined) || contextToTime;

  const { fromTime: _fromTime, toTime: _toTime, ...paramsWithoutTime } = params;
  return {
    ...paramsWithoutTime,
    addition: {
      ...params.addition,
      ...(utcFromTime && utcToTime ? { fromTime: utcFromTime, toTime: utcToTime } : {}),
    },
  };
};

/** 삭제된 타겟이어도 슬라이드를 열고 삭제 상태를 전달해야 하는 타입들 */
const ALLOW_DELETED_TYPES = new Set<KubernetesSlideDetailType>([
  TOTAL_DETAIL.NODE_LIST,
  TOTAL_DETAIL.POD_LIST,
  TOTAL_DETAIL.POD,
  TOTAL_DETAIL.NODE,
  TOTAL_DETAIL.DEPLOYMENT,
  TOTAL_DETAIL.STATEFUL_SET,
  TOTAL_DETAIL.REPLICA_SET,
  TOTAL_DETAIL.DAEMON_SET,
  TOTAL_DETAIL.JOB,
  TOTAL_DETAIL.EVENT,
  TOTAL_DETAIL.HISTORY_MANAGEMENT,
  TOTAL_DETAIL.K8S_CONTAINER,
  TOTAL_DETAIL.CONTAINER_HOST,
]);

const kubernetesSlideDetailTargetIdSchema = z.object({
  targetId: z.string().min(1),
  targetKind: z.nativeEnum(TargetStatusRequestTargetKind),
  fromTime: z.string().optional(),
  toTime: z.string().optional(),
});

export const kubernetesDetailRequiredMap = getRequiredMap(kubernetesSlideDetailTargetIdSchema);

const getKubernetesSlideTargetId = (
  params: KubernetesPanelInfo,
): z.infer<typeof kubernetesSlideDetailTargetIdSchema> => {
  const checkTargetKey = (type: KubernetesSlideDetailType): KubernetesSlideDetailAdditionKey => {
    switch (type) {
      case TOTAL_DETAIL.EVENT:
        return 'targetId';
      default:
        return 'uid';
    }
  };

  const checkTargetKind = (params: KubernetesPanelInfo): TargetStatusRequestTargetKind => {
    switch (params.type) {
      case TOTAL_DETAIL.EVENT:
      case TOTAL_DETAIL.HISTORY_MANAGEMENT: {
        // ? 성능분석쪽 테이블 API list에서 주는 kind랑 타겟 검증 api랑 targetKind양식이 다름 일단은 정의된 매핑 객체 재귀로 호출해서 사용;
        const mappedType = OBJECT_KIND_TO_KUBERNETES_DETAIL[params.addition?.kind?.toLowerCase()];
        if (
          mappedType === undefined ||
          mappedType === TOTAL_DETAIL.EVENT ||
          mappedType === TOTAL_DETAIL.HISTORY_MANAGEMENT
        ) {
          return '' as never;
        }
        return checkTargetKind({
          ...params,
          type: mappedType,
        });
      }
      case KUBER_DETAIL.POD:
        return TargetStatusRequestTargetKind.POD;
      case TOTAL_DETAIL.K8S_CONTAINER:
      case TOTAL_DETAIL.CONTAINER_HOST:
        return TargetStatusRequestTargetKind.CONTAINER;
      case KUBER_DETAIL.NODE:
        return TargetStatusRequestTargetKind.NODE;
      case KUBER_DETAIL.NAMESPACE:
        return TargetStatusRequestTargetKind.NAMESPACE;
      case KUBER_DETAIL.REPLICA_SET:
        return TargetStatusRequestTargetKind.REPLICASET;
      case KUBER_DETAIL.STATEFUL_SET:
        return TargetStatusRequestTargetKind.STATEFULSET;
      case KUBER_DETAIL.DAEMON_SET:
        return TargetStatusRequestTargetKind.DAEMONSET;
      case KUBER_DETAIL.JOB:
        return TargetStatusRequestTargetKind.JOB;
      case KUBER_DETAIL.CRON_JOB:
        return TargetStatusRequestTargetKind.CRONJOB;
      case KUBER_DETAIL.DEPLOYMENT:
        return TargetStatusRequestTargetKind.DEPLOYMENT;
      case KUBER_DETAIL.SERVICE:
      case KUBER_DETAIL.RESOURCE_INVENTORY_SERVICE:
        return TargetStatusRequestTargetKind.SERVICE;
      case KUBER_DETAIL.HPA:
        return TargetStatusRequestTargetKind.HPA;
      case KUBER_DETAIL.PERSISTENT_VOLUMES_CLAIMS:
        return TargetStatusRequestTargetKind.PERSISTENT_VOLUME_CLAIM;
      case KUBER_DETAIL.CONFIG_MAPS:
        return TargetStatusRequestTargetKind.CONFIGMAP;
      case KUBER_DETAIL.INGRESS:
        return TargetStatusRequestTargetKind.INGRESS;
      case KUBER_DETAIL.PERSISTENT_VOLUMES:
        return TargetStatusRequestTargetKind.PERSISTENT_VOLUME;
      case KUBER_DETAIL.STORAGE_CLASSES:
        return TargetStatusRequestTargetKind.STORAGE_CLASS;
      case KUBER_DETAIL.ROLES_BINDINGS:
        return TargetStatusRequestTargetKind.ROLE_BINDING;
      case KUBER_DETAIL.SERVICE_ACCOUNTS:
        return TargetStatusRequestTargetKind.SERVICE_ACCOUNT;
      case KUBER_DETAIL.ROLES:
        return TargetStatusRequestTargetKind.ROLE;
      case KUBER_DETAIL.OPENSHIFT_ROUTE:
        return TargetStatusRequestTargetKind.ROUTE;
      case KUBER_DETAIL.VIRTUAL_SERVICE:
        return TargetStatusRequestTargetKind.VIRTUAL_SERVICE;
      case KUBER_DETAIL.RESOURCE_INVENTORY_INGRESS:
        return TargetStatusRequestTargetKind.INGRESS;
      case KUBER_DETAIL.RESOURCE_INVENTORY_ROUTE:
        return TargetStatusRequestTargetKind.ROUTE;
      case KUBER_DETAIL.CLUSTER_ROLES:
        return TargetStatusRequestTargetKind.CLUSTER_ROLE;
      case KUBER_DETAIL.CLUSTER_ROLES_BINDINGS:
        return TargetStatusRequestTargetKind.CLUSTER_ROLE_BINDING;

      // 타입이 아니라 클러스터 > 해당 타입 목록 호출 디테일
      // (Virtual Service는 대응되는 targetKind가 없어 목록 디테일과 동일하게 CLUSTER로 처리)
      case KUBER_DETAIL.PERSISTENT_VOLUMES_CLAIMS_LIST:
      case KUBER_DETAIL.PERSISTENT_VOLUMES_LIST:
      case KUBER_DETAIL.STORAGE_CLASSES_LIST:
      case KUBER_DETAIL.NODE_LIST:
      case KUBER_DETAIL.NAMESPACE_LIST:
      case KUBER_DETAIL.POD_LIST:
      case KUBER_DETAIL.INGRESS_LIST:
      case KUBER_DETAIL.SERVICE_OBJECT_LIST:
      case KUBER_DETAIL.RESOURCE_INVENTORY_VIRTUAL_SERVICE:
        return TargetStatusRequestTargetKind.CLUSTER;

      default:
        return '' as never;
    }
  };

  const checkFromToTime = (params: KubernetesPanelInfo): { fromTime?: string; toTime?: string } => {
    const addition = params.addition as Record<string, string> | undefined;
    switch (params.type) {
      case TOTAL_DETAIL.EVENT:
        return {
          fromTime: addition?.eventTime,
        };
      case TOTAL_DETAIL.HISTORY_MANAGEMENT:
        return {
          fromTime: addition?.fromTime,
          toTime: addition?.toTime,
        };
      default:
        return { ...(addition?.eventTime ? { fromTime: addition.eventTime } : {}) };
    }
  };

  if (params.type === TOTAL_DETAIL.K8S_CONTAINER || params.type === TOTAL_DETAIL.CONTAINER_HOST) {
    return {
      targetId: params.container,
      targetKind: TargetStatusRequestTargetKind.CONTAINER,
      ...checkFromToTime(params),
    };
  }

  return {
    targetId: params.addition?.[checkTargetKey(params.type)],
    targetKind: checkTargetKind(params),
    ...checkFromToTime(params),
  };
};

export const useKubernetesSlideDetailAdapter = (rolePermissionKey?: RolePermissionKeyType) => {
  const slideDetailStore = getSlideDetailStore();
  const {
    addTabPanel,
    addSplitTabPanel,
    setIsDetailLoading,
    selectedTabComputed,
    setOpened,
    initTabPanel,
  } = slideDetailStore;
  const { isDetailLoading, selectedSlideTabPanel } = storeToRefs(slideDetailStore);

  const { isPermissionDenied } = useRolePermission();
  const { checkIsValidTarget } = useTargetPermissionCheck();
  const isSlideDetailOpened = ref(false);
  const { showErrorMsg } = useMessage();
  const { t } = useInternational();

  const validateKubernetesSlideDetail = async (
    params: KubernetesPanelInfo,
    options: {
      showErrorMessage?: boolean;
      checkPermission?: boolean;
      checkParams?: boolean;
      checkTarget?: boolean;
      isValidateDeletedTarget?: boolean;
    },
  ): Promise<{ reason: SlideDetailAdapterResultKey; isDeleted?: boolean }> => {
    const {
      checkPermission = true,
      checkParams = true,
      checkTarget = true,
      isValidateDeletedTarget = true,
    } = options;

    const validRolePermissionKey = rolePermissionKey || DetailTypeToPermissionKey?.[params.type];

    if (
      checkPermission &&
      validRolePermissionKey &&
      isPermissionDenied({ rolePermissionKey: validRolePermissionKey, type: 'detailSlide' })
    ) {
      return { reason: 'permission_denied' };
    }

    try {
      const { targetId, targetKind, fromTime, toTime } = getKubernetesSlideTargetId(params);

      if (checkTarget || checkParams) {
        const zodResult = kubernetesSlideDetailTargetIdSchema.safeParse({
          targetId,
          targetKind,
          fromTime,
          toTime,
        });
        if (!zodResult.success) {
          if (options.showErrorMessage) {
            showErrorMsg({
              msgStr: t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', {
                value: zodResult.error.issues.map((issue) => issue.path?.[0])?.join(', '),
              }),
            });
          }
          console.error(zodResult.error);
          return { reason: 'invalid_params' };
        }

        if (checkTarget) {
          const allowDeleted = ALLOW_DELETED_TYPES.has(params.type);
          const targetValidation = await checkIsValidTarget(
            {
              targetId,
              targetKind,
              fromTime,
              toTime,
            },
            {
              showErrorMessage: options.showErrorMessage,
              isValidateDeletedTarget: allowDeleted ? false : isValidateDeletedTarget,
            },
          );

          if (!targetValidation.valid) {
            console.warn(`${targetId} ${targetKind} is not valid target`);
            return { reason: targetValidation.reason };
          }

          if (targetValidation.reason === 'deleted') {
            return { reason: 'ok', isDeleted: true };
          }
        }
      }
    } catch {
      return { reason: 'invalid_params' };
    }

    return { reason: 'ok' };
  };

  /**
   *
   * @param args {
   *   isSplit: boolean; // splitSlideDetail  사용 여부
   * }
   */
  const openKubernetesSlideDetail = async (
    args: SlideDetailAdapterParams<KubernetesAdapterInput> & { isSplit?: boolean },
  ): Promise<SlideDetailAdapterResult> => {
    const {
      params,
      options: argsOptions,
      currentState,
      init,
      selectedTab,
      state,
      isSplit = false,
    } = args;

    const normalized = normalizeK8sInput(
      buildEnrichedParams(
        params,
        selectedSlideTabPanel.value?.addition as
          | (KubernetesPanelInfo['addition'] & {
              fromTime?: string;
              toTime?: string;
            })
          | undefined,
      ),
    );

    const options = merge(
      {},
      {
        runTargetValidation: true,
      },
      argsOptions,
    );

    const { reason, isDeleted } = await validateKubernetesSlideDetail(normalized, {
      showErrorMessage: options.showErrorMessage ?? true,
      checkPermission: options.runCheckPermission ?? true,
      checkTarget: options.runTargetValidation,
      isValidateDeletedTarget: options.isValidateDeletedTarget,
    });
    if (reason !== 'ok') return createSlideDetailResult(reason);

    const panelState = {
      ...state,
      selectedTab,
      isDeleted: isDeleted === true,
    };

    if (isSplit) {
      addSplitTabPanel({
        ...normalized,
        state: panelState,
      });
      isSlideDetailOpened.value = true;
    } else {
      const result = addTabPanel(
        {
          ...normalized,
          state: panelState,
          init,
        },
        currentState ?? {},
      );
      isSlideDetailOpened.value = slideDetailStore.isOpened;
      if (selectedSlideTabPanel.value?.state) {
        selectedSlideTabPanel.value.state.isDeleted = isDeleted === true;
      }
      return createSlideDetailResult(result);
    }
    return createSlideDetailResult('ok');
  };

  return {
    openKubernetesSlideDetail,
    setOpened,
    initTabPanel,
    isSlideDetailOpened,
    isDetailLoading,
    setIsDetailLoading,
    selectedSlideTabPanel,
    selectedTabComputed,
  };
};
